import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';
import { DEFAULT_CARD_BACK, type CardBack } from './cardBacks.js';

import { DEFAULT_CARD_FRONT, cardFrontOrDefault, type CardFront } from './cardFronts.js';
import { lastGameOrNull, type LastGame } from './lastGame.js';
import { ARCH_FINISHES, DEFAULT_FINISH, type ArchFinish } from './boardFinishes.js';

/** The card colors a player may choose. Storage ids, so British `colour` stays. */
export const DECK_STYLES = ['classic', 'four-colour'] as const;

export type DeckStyle = (typeof DECK_STYLES)[number];

/**
 * How long a bot appears to think before it moves.
 *
 * `natural` is the pacing the table was designed around; `instant` is for
 * practice, where the pause is just a wait; `slow` is for a player who reads
 * the felt between moves rather than racing it.
 */
export const BOT_SPEEDS = ['instant', 'natural', 'slow'] as const;

export type BotSpeed = (typeof BOT_SPEEDS)[number];

/**
 * Which way the player's own lines run on their screen in Cross-Cribbage.
 *
 * Display only (Mitch, 2026-10-01): the grid, the seating and the team colors
 * stay as the game assigned them, and `rows` or `columns` turns the drawing so
 * the player's lines run across or down. `flexible` draws the board as
 * assigned. Pass-and-play and the Cross lesson ignore it. See
 * `table/cross/transpose.ts`.
 */
export const CROSS_POSITIONS = ['rows', 'columns', 'flexible'] as const;

export type CrossPosition = (typeof CROSS_POSITIONS)[number];

/**
 * Game settings changed on this device that the server has not acknowledged:
 * whose they are, and which settings, by their wire names. Only those settings
 * are sent, so a change made on another device to a different setting is not
 * written back over. See `preferenceSync.ts`.
 */
export interface PendingPreferences {
  readonly player: string;
  readonly keys: readonly string[];
}

export interface SettingsState {
  /** The master mute. Kept beside `volume` so muting never loses your level. */
  sound: boolean;
  /** Master level, 0–1, as the player sets it. `AudioBus` owns the headroom. */
  volume: number;
  haptics: boolean;
  /**
   * Reduced motion runs the same fast-forward path the test harness uses, so
   * it can never bit-rot into a second, untested animation mode.
   */
  reducedMotion: boolean;
  deck: DeckStyle;
  /** The image every face-down card wears, across every kind of game. */
  cardBack: CardBack;
  /** Artwork for every face-up card, in Classic and Cross-Cribbage. */
  cardFront: CardFront;
  /** The Classic board's finish: what a hole is and what runs under the lanes. */
  boardFinish: ArchFinish;
  /**
   * The high-contrast Arcade palette: light surfaces and stronger text/edges.
   * Applied to the document so menus, portals and reviews follow it too.
   * The persisted key stays stable for existing installs.
   */
  highContrastFelt: boolean;
  /**
   * Pegging plays a card on one tap by default. Turned on, a tap arms the card
   * and a second press sends it — the shape the discard tray already has.
   */
  confirmPlay: boolean;
  /**
   * Play a card the player has no choice about without waiting for the tap.
   * Only ever fires when exactly one card is legal, so it can never pick.
   */
  autoPlayForced: boolean;
  botSpeed: BotSpeed;
  /** Which way your lines run on a Cross board. See `CROSS_POSITIONS`. */
  crossPosition: CrossPosition;
  /**
   * Send finished vs-bot games to the server so they count towards stats.
   *
   * Acts for every player, account or not: an anonymous player has a session
   * and statistics of their own now. Only vs-bot games go, because
   * pass-and-play has two people behind one session and no way to say whose
   * result it was, so it never leaves the device whatever this says. Turning it
   * off also clears what is already stored — see `docs/STATISTICS.md` §3.
   */
  syncOfflineGames: boolean;
  /**
   * The players who have turned the setting above off, by player id.
   *
   * The toggle alone is a device-wide answer, and the question is not a
   * device-wide one: "count my bot games" is one player saying something about
   * their own history. Without this, an anonymous player who opted out and kept
   * playing had their games uploaded the moment the next person to sign in on
   * the same install turned it on. `clearLocalGames(playerId)` already draws
   * that line — one player's answer is not an instruction about anybody else's.
   *
   * An opt-out list rather than a map because the default is on: a player with
   * no entry has never said otherwise, which is consent.
   */
  syncOptOutPlayerIds: readonly string[];
  /**
   * Set when an install upgrades from before the list existed with sync turned
   * off, and cleared the moment a player id is known.
   *
   * The list arrives empty on every upgrade, because v6 recorded the answer as
   * one device-wide boolean and there was no id attached to it. Left at that,
   * the next person to sign in and turn sync on would upload the games of the
   * player who had turned it off, which is the whole bug the list exists to
   * stop. So the refusal is remembered until there is somebody to attach it to.
   */
  syncOptOutLegacy: boolean;
  /**
   * The players who finished the Cross-Cribbage walkthrough on this device.
   *
   * The server keeps the same fact, and that copy is the one that follows a
   * player to another device. This one hides the invitation the moment the tour
   * ends, network or not, and tells the device to send the news again while the
   * server's answer still says otherwise. See `useCrossInvitation`.
   */
  crossTourPlayerIds: readonly string[];
  /**
   * The Game settings changed here that have not reached the server yet, or
   * null when the server has them all. Kept across a reload, so a change made
   * offline is sent on the next launch instead of being replaced by the
   * server's older copy. See `preferenceSync.ts`.
   */
  preferencesPending: PendingPreferences | null;
  language: string;
  /**
   * The last game started from Play, which the home's Play again door
   * replays. Null until a player launches one. Play setup writes it; nothing
   * else does. See `lastGame.ts`.
   */
  lastGame: LastGame | null;
  set<K extends keyof SettingsState>(key: K, value: SettingsState[K]): void;
}

/** What a fresh install gets, and what a reset goes back to. */
export const DEFAULT_SETTINGS = {
  sound: true,
  volume: 0.8,
  haptics: true,
  deck: 'classic',
  cardBack: DEFAULT_CARD_BACK,
  cardFront: DEFAULT_CARD_FRONT,
  boardFinish: DEFAULT_FINISH,
  highContrastFelt: false,
  confirmPlay: false,
  /*
   * On by default: the alternative is asking a player to tap the only card
   * they are allowed to play, every time, which is a tap that carries no
   * decision. A player who wants to place every card themselves can say so.
   */
  autoPlayForced: true,
  botSpeed: 'natural',
  crossPosition: 'flexible',
  /*
   * On by default. Every player has somewhere for a practice game to land, so
   * this is a question about whether they want it counted rather than about
   * whether there is an account to count it against.
   */
  syncOfflineGames: true,
  syncOptOutPlayerIds: [],
  syncOptOutLegacy: false,
  crossTourPlayerIds: [],
  preferencesPending: null,
  language: 'en',
  lastGame: null,
} as const satisfies Omit<SettingsState, 'set' | 'reducedMotion'>;

export const SETTINGS_VERSION = 10;

/*
 * Resolve storage for each operation rather than once at module evaluation.
 * The client is also imported by server-side tooling and tests where a browser
 * store may not exist yet; in a restricted WebView, even reading it can throw.
 * Settings still persist whenever localStorage is available, and degrade to an
 * in-memory session when it is not. Every persisted store on the device uses it.
 */
export const optionalStorage: StateStorage = {
  getItem(name) {
    try {
      return globalThis.localStorage?.getItem(name) ?? null;
    } catch {
      return null;
    }
  },
  setItem(name, value) {
    try {
      globalThis.localStorage?.setItem(name, value);
    } catch {
      /* Storage is optional; the live store still owns this session. */
    }
  },
  removeItem(name) {
    try {
      globalThis.localStorage?.removeItem(name);
    } catch {
      /* Storage is optional; the live store still owns this session. */
    }
  },
};

/* Declared above the store: its default reads the query at module load. */
/** The operating system's reduced-motion preference, as a media query. */
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * The operating system asks for less motion. False where there is no
 * `matchMedia`. The setting's default, and what `ui/reducedMotion.ts` reads
 * beside the setting.
 */
export function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      ...DEFAULT_SETTINGS,
      reducedMotion: prefersReducedMotion(),
      set: (key, value) => set({ [key]: value } as Partial<SettingsState>),
    }),
    {
      name: 'cribbage.settings',
      version: SETTINGS_VERSION,
      migrate: migrateSettings,
      merge: (persisted, current) => {
        const saved = persisted as Partial<SettingsState> | undefined;
        return {
          ...current,
          ...saved,
          cardFront: cardFrontOrDefault(saved?.cardFront),
          boardFinish: boardFinishOrDefault(saved?.boardFinish),
          lastGame: lastGameOrNull(saved?.lastGame),
        };
      },
      storage: createJSONStorage(() => optionalStorage),
    },
  ),
);

/**
 * Bring an older install forward.
 *
 * v1 carried a `quality` tier for the WebGL renderer, which no longer exists.
 * Left in place it would be an unreachable key that the store's own type does
 * not admit, so it is dropped rather than kept as a setting nothing reads.
 *
 * v2 had `sound` alone, with the level fixed in the audio bus. The keys added
 * since are merged in at their defaults rather than left undefined: a persisted
 * object is spread over the initial state, so a missing key would read as
 * `undefined` at every call site rather than falling back to anything.
 *
 * v3 had `hints` as a standing preference. Hints are now asked for one decision
 * at a time from the table itself, so the key has nothing left to mean and goes
 * the same way `quality` did.
 *
 * v4 predates offline sync. `syncOfflineGames` arrives at its default like any
 * other added key — which is on, and which is why the release it ships in has
 * to say so plainly rather than let it appear quietly.
 *
 * v5 predates selectable card backs. Its existing settings survive, and the
 * new back starts at the product default chosen from sketch U.
 *
 * v6 had the sync answer as one device-wide boolean, with no id attached, so
 * `syncOptOutPlayerIds` cannot be reconstructed and arrives empty. A device
 * whose toggle was off keeps it off through the upgrade, but "off" alone is not
 * enough: the next player to sign in and turn it on would carry the previous
 * player's games up with them. `syncOptOutLegacy` remembers the refusal until
 * there is a player id to record it against — see `claimLegacyOptOut`.
 *
 * v7 had separate table and score levels. Keep the overall volume and mute
 * choice, and drop the category controls in favor of the designed sound mix.
 *
 * v8 predates selectable fronts. Existing installs keep Arcade artwork.
 *
 * v9 predates the Cross playing position. It arrives as `flexible`, which
 * draws the board as assigned, the way every earlier build drew it.
 *
 * Exported because a migration is exactly the code that only ever runs on
 * someone else's machine, months after it was written.
 */
export function migrateSettings(persisted: unknown, version: number): SettingsState {
  if (version >= SETTINGS_VERSION || persisted === null || typeof persisted !== 'object') {
    return persisted as SettingsState;
  }
  const next = {
    ...(persisted as SettingsState & {
      quality?: unknown;
      hints?: unknown;
      tableVolume?: unknown;
      scoreVolume?: unknown;
    }),
  };
  delete next.quality;
  delete next.hints;
  delete next.tableVolume;
  delete next.scoreVolume;
  /*
   * `=== false`, not `!`. The type says boolean, but a persisted store from
   * before v5 has no `syncOfflineGames` key at all, and `!undefined` is true —
   * so the truthiness form marked every pre-v5 install as having refused, while
   * the spread below handed it the default of *on*. That device would show the
   * switch on and silently opt its first player out. The cast is what makes the
   * lie in the type visible rather than something eslint quietly rewrites.
   */
  const stored = next as Partial<SettingsState>;
  const legacyOptOut =
    version < 7 ? stored.syncOfflineGames === false : stored.syncOptOutLegacy === true;
  return { ...DEFAULT_SETTINGS, ...next, syncOptOutLegacy: legacyOptOut };
}

/**
 * Attach a pre-v7 device-wide opt-out to the first player this device is known
 * to have, and forget the marker.
 *
 * Called wherever a session is taken up, which is the earliest moment there is
 * an id to record the refusal against. Idempotent: once the marker is gone this
 * does nothing, so signing in and out does not keep adding people to the list.
 */
export function forgetLegacyOptOut(): void {
  if (useSettingsStore.getState().syncOptOutLegacy) {
    useSettingsStore.getState().set('syncOptOutLegacy', false);
  }
}

export function claimLegacyOptOut(playerId: string): void {
  const { syncOptOutLegacy, syncOptOutPlayerIds } = useSettingsStore.getState();
  if (!syncOptOutLegacy || playerId === '') return;
  // Both keys off the one snapshot, and both written from it. Reading the list back after
  // the first `set` would be reading a value this function had already invalidated.
  const claimed = syncOptOutPlayerIds.includes(playerId)
    ? syncOptOutPlayerIds
    : [...syncOptOutPlayerIds, playerId];
  useSettingsStore.getState().set('syncOptOutPlayerIds', claimed);
  useSettingsStore.getState().set('syncOptOutLegacy', false);
}

/**
 * Takes a deleted player out of the device settings that name players: the
 * Count my bot games opt-outs, the Cross walkthrough record and any Game
 * settings still waiting to reach the server. The device's own settings stay.
 */
export function forgetPlayerSettings(playerId: string): void {
  const state = useSettingsStore.getState();
  const optOuts = state.syncOptOutPlayerIds.filter((id) => id !== playerId);
  if (optOuts.length !== state.syncOptOutPlayerIds.length)
    state.set('syncOptOutPlayerIds', optOuts);
  const tours = state.crossTourPlayerIds.filter((id) => id !== playerId);
  if (tours.length !== state.crossTourPlayerIds.length) state.set('crossTourPlayerIds', tours);
  if (state.preferencesPending?.player === playerId) state.set('preferencesPending', null);
}

/** Remember that this player finished the Cross walkthrough here. Idempotent. */
export function markCrossTourDone(playerId: string): void {
  const { crossTourPlayerIds } = useSettingsStore.getState();
  if (playerId === '' || crossTourPlayerIds.includes(playerId)) return;
  useSettingsStore.getState().set('crossTourPlayerIds', [...crossTourPlayerIds, playerId]);
}

/** A saved finish this build still draws, or the default. */
export function boardFinishOrDefault(value: unknown): ArchFinish {
  return ARCH_FINISHES.find((finish) => finish === value) ?? DEFAULT_FINISH;
}
